import type { CommutePlan, DriverProgress, PickupStage, RideProgress } from "../shared/types";
import type { NativeJourney, NativeTone, NativeWatch, NativeWatchPickup, NativeWatchTrain } from "../shared/native-bridge";
import type { CardQuestion } from "./rider-journey";
import { cardStatus, clock, type Tone } from "./format";
import { riderPickupLine, type PickupTiming } from "./pickup";
import { stationLabel } from "../shared/labels";

/**
 * What the watch may show, worded here.
 *
 * The same rule as `nativeJourneyFrom`: every string on the wrist is composed by the web app, and
 * every time is an instant. What is new is that a watch has room for more than one page, so this
 * carries a deck and a pickup panel as well as the hero — but it does not carry a page layout.
 * Which of them are worth drawing is the watch's decision, and an empty list is how it is told.
 *
 * `now` is the Live Activity's own object rather than a second copy of its fields. The wrist and
 * the lock screen answer the same question, and wording it twice is how they come to disagree.
 */
export interface NativeWatchInput {
	now: NativeJourney;
	/** Live position on the train, for the line the island has no width for. */
	ride: RideProgress | null;
	/** The deck as the phone shows it, in the same order. */
	plans: CommutePlan[];
	pinnedId: string | null;
	recommendedId: string | null;
	destinationIndex: number;
	mode: CardQuestion;
	hasAccessLeg: boolean;
	pickup: NativeWatchPickupInput | null;
	nowMs: number;
	timezone: string;
}

/** The afternoon meeting as the rider's own screen states it. */
export interface NativeWatchPickupInput {
	/** Which of `PickupBlock`'s four situations this is. */
	kind: "offered" | "paused" | "active" | "completed";
	progress: DriverProgress;
	timing: PickupTiming;
	driverMeters: number | null;
	/** The last rung the driver was actually pushed, so "he knows" is a fact and not a hope. */
	told: PickupStage | null;
	atShuttleStop: boolean;
	/** The agreed kerb, already named. Null until one has been set. */
	spot: string | null;
	trainArrivalMs: number | null;
	stationName: string | null;
	nowMs: number;
	timezone: string;
}

/** How many cards are worth a wrist. Past this a swipe is a search, and the phone is the better tool. */
const DECK_LIMIT = 6;

export function nativeWatchFrom(input: NativeWatchInput): NativeWatch {
	return {
		atMs: input.nowMs,
		now: input.now,
		context: contextLine(input),
		trains: deckFrom(input),
		pickup: input.pickup ? pickupPanel(input.pickup) : null,
	};
}

/**
 * The line the Dynamic Island cannot carry.
 *
 * Only while he is actually on a train: everywhere else the hero already says the one thing worth
 * knowing, and a second line repeating it in other words is how a glance becomes a read. Worded the
 * same way `RiderMonitor` words it, because David and Eben should be reading the same sentence.
 */
function contextLine(input: NativeWatchInput): string | null {
	const ride = input.ride;
	if (input.mode !== "onboard" || !ride) {
		return null;
	}
	const next = ride.nextStopName ? `${ride.arriving ? "Arriving" : "Next"} ${stationLabel(ride.nextStopName)}` : null;
	const minutes = ride.destinationEtaMs == null ? null : Math.round((ride.destinationEtaMs - input.nowMs) / 60_000);
	const arriving = ride.destinationName == null || minutes == null
		? null
		: `${stationLabel(ride.destinationName)} in ${minutes} ${Math.abs(minutes) === 1 ? "min" : "mins"}`;
	const parts = [next, arriving].filter((part): part is string => part != null);
	return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * The deck, from the same `cardStatus` the phone's cards are drawn from.
 *
 * The margin and the colour come from there rather than being worked out again, so a train that is
 * amber on the phone cannot be green on the wrist. What the watch shows instead of the hero is the
 * departure itself: a number of minutes decays between updates, and a clock time does not.
 *
 * Unreachable trains are kept and marked rather than dropped. Which train he catches is not settled
 * until he boards, and "you have probably missed that one" is a real answer to a glance.
 */
function deckFrom(input: NativeWatchInput): NativeWatchTrain[] {
	return input.plans.slice(0, DECK_LIMIT).map((plan) => {
		const status = cardStatus(plan, input.hasAccessLeg, input.nowMs, input.timezone, input.mode, input.destinationIndex);
		const alighting = plan.destinations[input.destinationIndex] ?? plan.destinations[0] ?? null;
		return {
			planId: plan.id,
			when: clock(plan.boarding.predictedMs, input.timezone),
			lead: alighting ? stationLabel(alighting.stopName) : plan.headsign,
			detail: alighting?.predictedMs == null ? null : `arrives ${clock(alighting.predictedMs, input.timezone)}`,
			note: status.note ? `${status.note.value} ${status.note.label}` : null,
			tone: toneOf(status.tone),
			pinned: plan.id === input.pinnedId,
			recommended: plan.id === input.recommendedId,
			reachable: plan.feasible,
			countdownToMs: plan.boarding.predictedMs,
		};
	});
}

/**
 * The pickup, in the words Eben's own screen uses — which never name David.
 *
 * The countdown follows whichever half of the meeting is still unknown: before the car sets off
 * that is the train, and once it is moving it is the car. Both are instants already.
 */
function pickupPanel(input: NativeWatchPickupInput): NativeWatchPickup {
	const line = riderPickupLine(input.kind, input.progress, input.timing, input.driverMeters, input.nowMs, input.told, input.atShuttleStop);
	const underway = input.progress === "driving";
	const train = input.trainArrivalMs == null
		? null
		: `${input.stationName ? `${stationLabel(input.stationName)} ` : ""}${clock(input.trainArrivalMs, input.timezone)}`;
	return {
		lead: line.status,
		value: null,
		detail: line.wait || train,
		note: null,
		tone: line.tone,
		countdownToMs: underway ? input.timing.driverArrivalMs : input.trainArrivalMs,
		spot: input.spot,
	};
}

/** The card's palette has a fifth entry the wire does not; `neutral` is the absence of a verdict. */
function toneOf(tone: Tone): NativeTone {
	return tone === "neutral" ? "" : tone;
}
